import type { ApiInspectionsV1LoadsListParams, Load } from 'api';
import {
  useApiInspectionsV1LoadsList,
  useApiInspectionsV1LoadsMarkInspectionNotNeededPartialUpdate,
  useQueryClient,
} from 'api';
import type { Dayjs } from 'date';
import { useToggle } from 'features';
import { useTranslation } from 'i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GridFilterModel, GridPaginationModel, GridSortModel } from 'ui';
import { Box, Button, DataGrid, Drawer, Page, Section, Stack } from 'ui';

import { DealDetails } from '../../../deal';
import { createFromFilter, createToFilter, initialFromFilter, TimeFilter } from '../../../time-filter';
import { LoadDetails } from '../../components/LoadDetails/LoadDetails';
import { useSearchedLoad } from '../../hooks/useSearchedLoad/useSearchedLoad';
import { columns } from './columns';
import { TIME_FILTER_FIELD_NAME } from './constants';

const DEFAULT_PAGE_SIZE = 25;
const SORTABLE_FIELDS = new Set([
  'deal_id',
  'destination',
  'end_processor',
  'estimated_collection_time',
  'id',
  'loading_site',
  'material',
  'name',
  'producer',
  'risk_score',
  'status',
]);

const mapFilterModelToParams = (filterModel: GridFilterModel): Partial<ApiInspectionsV1LoadsListParams> => {
  const params: Record<string, unknown> = {};

  filterModel.items.forEach((item) => {
    if (!item.field || item.value === undefined || item.value === null || item.value === '') {
      return;
    }

    if (item.operator !== 'equals' && item.operator !== 'is') {
      return;
    }

    switch (item.field) {
      case 'deal_id': {
        const value = Number(item.value);

        if (!Number.isNaN(value)) {
          params.deal_id = [value];
        }
        return;
      }
      case 'id': {
        const value = Number(item.value);

        if (!Number.isNaN(value)) {
          params.id = value;
        }
        return;
      }
      case 'inspection_id': {
        const value = item.value === true || item.value === 'true';
        params.inspection_id = value;
        return;
      }
      default: {
        const field = item.field as keyof ApiInspectionsV1LoadsListParams;
        params[field] = item.value;
      }
    }
  });

  return params as Partial<ApiInspectionsV1LoadsListParams>;
};

export const LoadsList = () => {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: searchedLoad } = useSearchedLoad();

  const { open, isOpen, close } = useToggle();

  const [selectedLoad, setSelectedLoad] = useState<Load | null>(null);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'deal_id',
      sort: 'desc',
    },
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [timeFilters, setTimeFilters] = useState<
    Pick<ApiInspectionsV1LoadsListParams, 'estimated_collection_time__gte' | 'estimated_collection_time__lte'>
  >(() => ({
    estimated_collection_time__gte: initialFromFilter(TIME_FILTER_FIELD_NAME).value as string,
  }));

  useEffect(() => {
    if (searchedLoad) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedLoad(searchedLoad);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      open();
    }
  }, [open, searchedLoad]);

  const handleTimeFilterChange = useCallback(({ from, to }: { from: Dayjs; to: Dayjs }) => {
    setTimeFilters({
      estimated_collection_time__gte: createFromFilter(from, TIME_FILTER_FIELD_NAME).value as string,
      estimated_collection_time__lte: createToFilter(to, TIME_FILTER_FIELD_NAME).value as string,
    });
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const filterParams = useMemo(() => mapFilterModelToParams(filterModel), [filterModel]);

  const orderParams = useMemo(() => {
    return sortModel
      .filter((item) => item.sort && SORTABLE_FIELDS.has(item.field))
      .map((item) =>
        item.sort === 'desc' ? `-${item.field}` : item.field
      ) as ApiInspectionsV1LoadsListParams['order'];
  }, [sortModel]);

  const queryParams = useMemo<ApiInspectionsV1LoadsListParams>(() => {
    const params: ApiInspectionsV1LoadsListParams = {
      page: paginationModel.page + 1,
      page_size: paginationModel.pageSize,
      ...filterParams,
    };

    if (timeFilters.estimated_collection_time__gte) {
      params.estimated_collection_time__gte = timeFilters.estimated_collection_time__gte;
    }

    if (timeFilters.estimated_collection_time__lte) {
      params.estimated_collection_time__lte = timeFilters.estimated_collection_time__lte;
    }

    if (orderParams && orderParams.length > 0) {
      params.order = orderParams;
    }

    return params;
  }, [filterParams, orderParams, paginationModel.page, paginationModel.pageSize, timeFilters]);

  const loadsQuery = useApiInspectionsV1LoadsList(queryParams, {
    query: {
      placeholderData: (previousData) => previousData,
    },
  });
  const { data, isFetching, queryKey } = loadsQuery;

  const { mutate: markInspectionNotNeededMutation } = useApiInspectionsV1LoadsMarkInspectionNotNeededPartialUpdate({
    mutation: {
      onSuccess: (updatedLoad) => {
        setSelectedLoad(updatedLoad);
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  const rows = data?.results ?? [];
  const rowCount = data?.count ?? 0;

  const markInspectionNotNeeded = (loadId: number) => {
    markInspectionNotNeededMutation({ id: loadId });
  };

  return (
    <Page>
      <Drawer open={isOpen} anchor="right" onClose={close}>
        <Stack
          direction="column"
          sx={{
            gap: 2,
          }}
        >
          <Drawer.Header
            title={
              selectedLoad
                ? t('loadsList.drawer.titleWithName', { values: { loadName: selectedLoad.name } })
                : t('loadsList.drawer.title')
            }
          />
          <div>
            <Button
              size="small"
              onClick={() => {
                navigate(`/ground-control/inspections/create?dealId=${selectedLoad?.deal_id}`);
              }}
            >
              {t('loadsList.actions.createInspection')}
            </Button>
          </div>
          <Box
            sx={{
              mb: 2,
            }}
          >
            <LoadDetails load={selectedLoad} onNotNeededClick={markInspectionNotNeeded} />
          </Box>
          {selectedLoad ? <DealDetails dealId={selectedLoad.deal_id} /> : null}
        </Stack>
      </Drawer>
      <Section title={t('loadsList.sectionTitle')}>
        <TimeFilter onDateChange={handleTimeFilterChange} />
        <DataGrid<Load>
          autoHeight
          columns={columns}
          showColumnVerticalBorder
          showCellVerticalBorder
          filterDebounceMs={500}
          density="standard"
          sx={{ overflowX: 'scroll' }}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            setPaginationModel(model);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPaginationModel((current) => ({ ...current, page: 0 }));
          }}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={(model) => {
            setFilterModel(model);
            setPaginationModel((current) => ({ ...current, page: 0 }));
          }}
          loading={isFetching}
          rowCount={rowCount}
          rows={rows}
          onRowClick={(params) => {
            setSelectedLoad(params.row);
            open();
          }}
        />
      </Section>
    </Page>
  );
};
